import React, { useEffect, useState } from "react";
import { Star, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import {
  addReview,
  getReviewsForProduct,
  REVIEWS_CHANGE_EVENT,
} from "@/lib/reviews";

// Interactive star control. Pass `readOnly` to render as a static row.
const StarRating = ({
  value = 0,
  onChange,
  size = 22,
  readOnly = false,
  testIdPrefix,
}) => {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div
      className="inline-flex items-center gap-1"
      role={readOnly ? undefined : "radiogroup"}
      aria-label="Star rating"
    >
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= display;
        return (
          <button
            key={i}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && onChange?.(i)}
            onMouseEnter={() => !readOnly && setHover(i)}
            onMouseLeave={() => !readOnly && setHover(0)}
            aria-label={`${i} star${i > 1 ? "s" : ""}`}
            aria-pressed={!readOnly && value === i}
            data-testid={
              readOnly ? undefined : testIdPrefix ? `${testIdPrefix}-${i}` : undefined
            }
            className={`${
              readOnly
                ? "cursor-default"
                : "cursor-pointer transition-transform hover:scale-110 active:scale-95"
            }`}
          >
            <Star
              size={size}
              strokeWidth={1.6}
              fill={filled ? "#E0B53C" : "transparent"}
              stroke={filled ? "#E0B53C" : "#9CA3AF"}
            />
          </button>
        );
      })}
    </div>
  );
};

const formatRelative = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

export const ReviewSection = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, avg: 0 });
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);

  const reload = async () => {
    if (!productId) return;
    try {
      const list = await getReviewsForProduct(productId);
      setReviews(list);
      if (list.length === 0) {
        setStats({ count: 0, avg: 0 });
      } else {
        const sum = list.reduce((a, r) => a + (Number(r.rating) || 0), 0);
        setStats({ count: list.length, avg: sum / list.length });
      }
    } catch {
      /* keep last-known state */
    }
  };

  useEffect(() => {
    if (!productId) return;
    reload();
    const onChange = () => reload();
    window.addEventListener(REVIEWS_CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener(REVIEWS_CHANGE_EVENT, onChange);
    };
  }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Please pick a star rating");
      return;
    }
    if (name.trim().length < 2) {
      toast.error("Please enter your name");
      return;
    }
    try {
      await addReview({ productId, name, comment, rating });
      setName("");
      setComment("");
      setRating(0);
      toast.success("Thanks for your review!", { duration: 1800 });
    } catch (err) {
      toast.error(err?.message || "Could not save your review");
    }
  };

  if (!productId) return null;

  return (
    <section
      data-testid="review-section"
      className="border-t border-black/5 pt-5 mt-5 space-y-5"
    >
      {/* Header — average rating + count */}
      <div className="flex items-center gap-2">
        <MessageSquare size={14} strokeWidth={1.8} className="text-[#064E3B]" />
        <span className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
          Reviews
        </span>
        <span
          className="text-[11px] text-neutral-500"
          data-testid="review-count"
        >
          ({stats.count})
        </span>
      </div>

      <div className="flex items-center gap-4">
        <div
          className="font-display text-4xl text-[#0B0C10] leading-none"
          data-testid="review-avg-value"
        >
          {stats.count > 0 ? stats.avg.toFixed(1) : "—"}
        </div>
        <div>
          <StarRating value={Math.round(stats.avg)} readOnly size={18} />
          <p className="text-[11px] text-neutral-500 mt-1">
            {stats.count === 0
              ? "Be the first to rate"
              : `${stats.count} review${stats.count === 1 ? "" : "s"}`}
          </p>
        </div>
      </div>

      {/* Write a review form */}
      <form
        onSubmit={submit}
        className="rounded-2xl border border-black/10 bg-white p-4 space-y-3"
        data-testid="review-form"
      >
        <div className="space-y-1.5">
          <span className="block text-[11px] uppercase tracking-[0.18em] text-neutral-600 font-medium">
            Your rating
          </span>
          <StarRating
            value={rating}
            onChange={setRating}
            size={26}
            testIdPrefix="review-star"
          />
        </div>

        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.18em] text-neutral-600 mb-1.5 font-medium">
            Name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Aisha K."
            maxLength={50}
            data-testid="review-name-input"
            className="w-full bg-white border border-black/10 focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-xl px-3 py-2.5 text-sm transition-all"
          />
        </label>

        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.18em] text-neutral-600 mb-1.5 font-medium">
            Review (optional)
          </span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share a few words about the fabric, fit, or delivery…"
            rows={3}
            maxLength={500}
            data-testid="review-comment-input"
            className="w-full bg-white border border-black/10 focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-xl px-3 py-2.5 text-sm resize-none transition-all"
          />
        </label>

        <button
          type="submit"
          data-testid="review-submit-btn"
          className="inline-flex items-center gap-2 bg-[#0B0C10] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all"
        >
          <Send size={14} strokeWidth={1.8} />
          Submit review
        </button>
      </form>

      {/* Review list */}
      {reviews.length > 0 && (
        <ul className="space-y-3" data-testid="review-list">
          {reviews.map((r) => (
            <li
              key={r.id}
              data-testid={`review-item-${r.id}`}
              className="rounded-2xl bg-neutral-50 border border-black/5 p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-sm text-[#0B0C10] truncate">
                    {r.name}
                  </p>
                  <p className="text-[10px] text-neutral-500 uppercase tracking-[0.14em]">
                    {formatRelative(r.createdAt)}
                  </p>
                </div>
                <StarRating value={r.rating} readOnly size={14} />
              </div>
              {r.comment && (
                <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
                  {r.comment}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
